import React, { useState, useRef, useEffect } from "react";
import Timer from "./Timer";
import Controls from "./Controls";
import Indicator from "./Indicator";
import SettingsDigits from "./SettingsDigits";
import Help from "./Help";
import Menu from "./Menu";
import "../assets/FlowTimer.css";

const FlowTimer = () => {
  const [isFocusRunning, setIsFocusRunning] = useState(false);
  const [isRestRunning, setIsRestRunning] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const [focusTime, setFocusTime] = useState(25*60); // Initial 25 minutes
  const [restTime, setRestTime] = useState(5*60); // Initial 5 minutes
  const [selectedTimer, setSelectedTimer] = useState("focus"); // Track which timer is selected
  const [shouldStopAudio, setShouldStopAudio] = useState(false);
  const [reset, setReset] = useState(false); // Track reset state
  const [isPomodoroMode, setIsPomodoroMode] = useState(true); // Track Pomodoro mode state
  const [startedFocusTime, setStartedFocusTime] = useState(1500); // Stores focus time when started
  const [startedRestTime, setStartedRestTime] = useState(300); // Stores rest time when started
  const audioRef = useRef(null);

  // Preset function variables
  const [currentPreset, setCurrentPreset] = useState(0); // Track which preset is currently active: 1, 2, or 3
  const [userPresetFocus, setUserPresetFocus] = useState(60 * 60);  // Store the user-defined times for Preset 3.
  const [userPresetRest, setUserPresetRest] = useState(30 * 60); // Default: 60 min focus, 30 min rest
  const [presetSavedFlash, setPresetSavedFlash] = useState(false);


  // Which setting we are editing: 0=none, 1=brightness, 2=volume, 3=tones
  const [currentSetting, setCurrentSetting] = useState(0);
  const inSettings = currentSetting > 0

  // Store the chosen values for each setting (default brightness=5, volume=5, tone=1, for example)
  const [brightness, setBrightness] = useState(5);
  const [volume, setVolume] = useState(5);
  const [tone, setTone] = useState(1);

  // Actually do something with the brightness
  const brightnessFactor = brightness * 0.2; // 1 =>0.2 ... 5=>1.0

  //On or Off State
  const [isDeviceOn, setIsDeviceOn] = useState(false)


  // Set the tone path depending one the tone setting
  const getToneSrc = (toneNumber) => {
    switch (toneNumber) {
      case 1: return "/tones/FocusFlo Tone 1 - Upbeat.mp3";
      case 2: return "/tones/FocusFlo Tone 2 - Chime.mp3";
      case 3: return "/tones/FocusFlo Tone 3 - Beep.mp3";
      case 4: return "/tones/FocusFlo Tone 4 - SingingBowl.mp3";
      case 5: return "/tones/FocusFlo Tone 5 - LofiShort.mp3";
      case 6: return "/tones/FocusFlo Tone 6 - Rain.mp3";
      case 7: return "/tones/FocusFlo Tone 7 - Simple.mp3";
      case 8: return "/tones/FocusFlo Tone 8 - Alert.mp3";
      default: return "/tones/FocusFlo Tone 1 - Upbeat.mp3";
    }
  };

  useEffect(() => {
    const savedFocus = localStorage.getItem("userPresetFocus");
    const savedRest = localStorage.getItem("userPresetRest");
  
    // If they exist, parse them back into numbers and update state
    if (savedFocus) {
      setUserPresetFocus(Number(savedFocus));
    }
    if (savedRest) {
      setUserPresetRest(Number(savedRest));
    }
  }, []);

  const playAlertAndFlash = (iconType) => {
    setIsFlashing(true); // Start flashing
    setTimeout(() => {
      setIsFlashing(false); // Stop flashing after 2 seconds
      setShouldStopAudio(true); // Stop audio after flashing
      setTimeout(() => setShouldStopAudio(false), 100); // Reset audio state
    }, 1950); // Duration of flashing
  };

  const handleFocusTimerEnd = () => {
    if (isPomodoroMode) {
      if (startedRestTime !== 0){
        playAlertAndFlash("focus");
        setTimeout(() => {
        console.log("Transitioning from Focus to Rest");
        setIsFocusRunning(false);
        setSelectedTimer("rest");
        setIsRestRunning(true); // Start the rest timer
      }, 2000);
      } else {
        setIsFocusRunning(false);
        setIsFlashing(true);
      }
    } else {
      setIsFocusRunning(false);
      setIsFlashing(true);
    }
  };
  
  const handleRestTimerEnd = () => {
    if (isPomodoroMode) {
      if (startedFocusTime !== 0){
      playAlertAndFlash("rest");
      setTimeout(() => {
        console.log("Transitioning from Rest to Focus");
        setIsRestRunning(false);
        setSelectedTimer("focus");
        setIsFocusRunning(true); // Start the focus timer
      }, 2000);
      } else {
        setIsRestRunning(false);
        setIsFlashing(true);
      }

    } else {
      setIsRestRunning(false);
      setIsFlashing(true);
    }
  };

  // Start/Pause button logic
  const handleStartPause = () => {
    // Save the starting times when the user starts the timer
    if (!isFocusRunning && !isRestRunning) {
      setStartedFocusTime(focusTime);
      setStartedRestTime(restTime);
    }

    // Stop the audio immediately
    setShouldStopAudio(true);
    setTimeout(() => setShouldStopAudio(false), 100);

    // If isFlashing, reset the timer
    if (isFlashing) {
      handleReset();
    } else {
      // Toggle the timer running state
      if (selectedTimer === "focus") {
        setIsFocusRunning(!isFocusRunning);
      } else {
        setIsRestRunning(!isRestRunning);
      }
    }
  };

  // Reset button logic
  const handleReset = () => {
    // Prevent reset if any timer is running
    if (isFocusRunning || isRestRunning) {
      return;
    }

    setIsFlashing(false);
    setFocusTime(startedFocusTime); // Reset focus to 25 minutes
    setRestTime(startedRestTime); // Reset rest to 5 minutes
    setShouldStopAudio(true); // Trigger stopping the audio
    setReset(true); // Trigger reset in Timer components
    setTimeout(() => setReset(false), 100); // Slightly increase timeout for better processing
  };

  // Toggle Timer between Focus and Rest
  const handleToggle = () => {
    // Start flashing
    setSelectedTimer((prev) => (prev === "focus" ? "rest" : "focus"));
    setIsFlashing(true);
    
    setTimeout(() => {
      // Turn flashing off
      setIsFlashing(false);

    }, 3000);
  };

  // **Long Reset: Resets to 00:00**
  const handleLongReset = () => {
    // Prevent reset if any timer is running
    if (isFocusRunning || isRestRunning) {
      return;
    }

    setIsFlashing(false);
    // Logic for pomodoro mode vs single timer mode
    if (isPomodoroMode){
      setFocusTime(0); // Reset focus to 0
      setRestTime(0); // Reset rest to 0
    } else {
      if (selectedTimer === "focus"){
        setFocusTime(0); // Reset focus to 0
      }else{
        setRestTime(0); // Reset focus to 0
      }
    }

    setShouldStopAudio(true); // Trigger stopping the audio
    setReset(true); // Trigger reset in Timer components
    setTimeout(() => setReset(false), 100); // Slightly increase timeout for better processing
  };

  // Toggle through timer presets

  // Called on normal click of the "preset" button
  const handlePreset = () => {
    // Cycle currentPreset 1 -> 2 -> 3 -> 1 -> ...
    const newPreset = currentPreset === 3 ? 1 : currentPreset + 1;
    setCurrentPreset(newPreset);

    switch (newPreset) {
      case 1:
        // Preset 1: 25 focus, 5 rest
        setFocusTime(25 * 60);
        setRestTime(5 * 60);
        break;
      case 2:
        // Preset 2: 45 focus, 15 rest
        setFocusTime(45 * 60);
        setRestTime(15 * 60);
        break;
      case 3:
        // Preset 3: user-defined
        setFocusTime(userPresetFocus);
        setRestTime(userPresetRest);
        break;
      default:
        break;
    }
  };

  // Called when holding the preset button for 3 seconds
  const handleSavePreset3 = () => {
    // Save the current times
    setUserPresetFocus(focusTime);
    setUserPresetRest(restTime);
    localStorage.setItem("userPresetFocus", focusTime.toString());
    localStorage.setItem("userPresetRest", restTime.toString());
  
    // Switch to preset 3
    setCurrentPreset(3);
  
    // Flash for 1 second
    setPresetSavedFlash(true);
    setTimeout(() => {
      setPresetSavedFlash(false);
    }, 1000);
  };

  // Adjust focus time
  const incrementFocusTime = (seconds) => {
    setReset(false); // Ensure reset doesn't conflict
    setFocusTime((prev) => {
      const newTime = prev + seconds;
      return newTime > 199 * 60 + 55 ? 0 : newTime; // Loop back to 5 seconds if max is exceeded
    });
  };

  const decrementFocusTime = (seconds) => {
    setReset(false); // Ensure reset doesn't conflict
    setFocusTime((prev) => {
      const newTime = prev - seconds;
      return newTime < 0 ? 199 * 60 + 55 : newTime; // Loop back to 199:55 if below 5 seconds
    });
  };

  // Adjust rest time
  const incrementRestTime = (seconds) => {
    setReset(false); // Ensure reset doesn't conflict
    setRestTime((prev) => {
      const newTime = prev + seconds;
      return newTime > 199 * 60 + 55 ? 0 : newTime; // Loop back to 5 seconds if max is exceeded
    });
  };

  const decrementRestTime = (seconds) => {
    setReset(false); // Ensure reset doesn't conflict
    setRestTime((prev) => {
      const newTime = prev - seconds;
      return newTime < 0 ? 199 * 60 + 55 : newTime; // Loop back to 199:55 if below 5 seconds
    });
  };

  const handlePomodoroMode = () => {
    setIsPomodoroMode(!isPomodoroMode); // Toggle Pomodoro mode

  };

  // Enter settings mode
  const enterSettingsMode = () => setCurrentSetting(1);

  // Exit settings mode
  const exitSettingsMode = () => setCurrentSetting(0);

   // Cycle through brightness -> volume -> tone -> exit
   const handleNextSetting = () => {
    if (currentSetting === 1) setCurrentSetting(2);
    else if (currentSetting === 2) setCurrentSetting(3);
    else if (currentSetting === 3) setCurrentSetting(1);
  };

  // Decide which setting level to display in digits
  const getSettingsValue = () => {
    switch (currentSetting) {
      case 1: return brightness;
      case 2: return volume;
      case 3: return tone;
      default: return 0; // or some fallback
    }
  };

  


  return (
    <div className="flowTimerContainer">
      {/* Audio Element for the Timer Tone */}
      <audio ref={audioRef} src={getToneSrc(tone)} />

      <Menu 
        isDeviceOn={isDeviceOn}
      />

      {/* Indicators */}
      <Indicator
        selectedTimer={selectedTimer}
        isPomodoroMode={isPomodoroMode}
        currentSetting={currentSetting}
        brightnessFactor={brightnessFactor}
        volume={volume}
        isDeviceOn={isDeviceOn}
      />
      
      {/* If NOT in settings => show Timers/Indicators */}
      
        <>
          {/* Focus Timer */}
          <Timer
            pomodoroEnabled={isPomodoroMode}
            initialTime={focusTime}
            initialFocus={startedFocusTime}
            isRunning={isFocusRunning}
            onEnd={handleFocusTimerEnd}
            isFlashing={
              // Old logic for focus
              (selectedTimer === "focus" && isFlashing)
              // OR new "preset-saved" flash
              || presetSavedFlash
            }
            shouldStopAudio={shouldStopAudio}
            timerName="focusTimer"
            reset={reset} // Pass reset state
            brightnessFactor={brightnessFactor}
            volume={volume}
            tone={tone}
            inSettings={inSettings}
            isDeviceOn={isDeviceOn}
          />

          {/* Rest Timer */}
          <Timer
            pomodoroEnabled={isPomodoroMode}
            initialTime={restTime}
            initialRest={startedRestTime}
            isRunning={isRestRunning}
            onEnd={handleRestTimerEnd}
            isFlashing={
              // Old logic for rest
              (selectedTimer === "rest" && isFlashing)
              // OR new "preset-saved" flash
              || presetSavedFlash
            }
            shouldStopAudio={shouldStopAudio}
            timerName="restTimer"
            reset={reset} // Pass reset state
            brightnessFactor={brightnessFactor}
            volume={volume}
            tone={tone}
            inSettings={inSettings}
            isDeviceOn={isDeviceOn}
          />
        </>
      

      {/* If in settings => show digits for current setting (e.g., "05" if brightness=5) */}
      
      <SettingsDigits
        style={{ display: isDeviceOn && inSettings ? "block" : "none" }}
        value={getSettingsValue()} 
        isFlashing={false} 
        brightnessFactor={brightnessFactor}
        volume={volume}
        tone={tone}
        inSettings={inSettings}
      />
      

      {/* Controls */}
      <Controls
        isRunning={selectedTimer === "focus" ? isFocusRunning : isRestRunning}
        handleStartPause={handleStartPause}
        handleReset={handleReset}
        handleLongReset={handleLongReset}
        handleToggle={handleToggle}
        handlePreset={handlePreset}
        handleSavePreset3={handleSavePreset3}
        selectedTimer={selectedTimer}
        incrementFocusTime={incrementFocusTime}
        decrementFocusTime={decrementFocusTime}
        incrementRestTime={incrementRestTime}
        decrementRestTime={decrementRestTime}
        handlePomodoroMode={handlePomodoroMode}
        
        // On/off
        setIsDeviceOn={setIsDeviceOn}
        isDeviceOn={isDeviceOn}


        // Settings logic
        currentSetting={currentSetting}
        enterSettingsMode={enterSettingsMode}
        exitSettingsMode={exitSettingsMode} 
        brightness={brightness}
        setBrightness={setBrightness}
        volume={volume}
        setVolume={setVolume}
        tone={tone}
        setTone={setTone}
        handleNextSetting={handleNextSetting}
      />

      <Help 
        isDeviceOn={isDeviceOn}
 
      />

    </div>
  );
};

export default FlowTimer;
